.dispatchRow {
    display: grid;
    /* Each row is its own grid, so columns only line up across rows when every track
       is identical. The schedule column is a fixed width — sized for the widest date
       range plus an OVERDUE pill — so multi-day labels never overflow and the right
       edge of every row's date stays aligned. */
    grid-template-columns:
        4px minmax(0, 1.15fr) minmax(0, 1fr) var(--dispatch-schedule-col, 11.5rem)
        1.25rem;
    column-gap: 0.65rem;
    align-items: center;
    padding: 0.4rem 0 0.75rem 15px;
    border-bottom: 1px solid var(--mantine-color-gray-1);
    cursor: pointer;
    min-height: 2.35rem;
    transition: background-color 100ms ease;
}

.dispatchRowAlternate {
    background-color: var(--mantine-color-gray-0);
}

.dispatchRowInteractive:hover {
    background-color: color-mix(
        in srgb,
        var(--mt-signal-attention-0) 35%,
        var(--mantine-color-gray-0)
    );
}

.dispatchRowNotifyDrivers.dispatchRowInteractive:hover {
    background-color: color-mix(
        in srgb,
        var(--mt-signal-pending-0) 25%,
        var(--mantine-color-gray-0)
    );
}

.dispatchRowInteractive:hover .rowChevron {
    color: var(--mantine-color-gray-9);
    transform: translateX(2px);
}

.dispatchRow:focus-visible {
    outline: 2px solid var(--mt-focus-ring);
    outline-offset: -2px;
}

.dispatchRowNeedsAction,
.dispatchRowWithMeta {
    min-height: 2.65rem;
    align-items: start;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
}

.dispatchRowNotifyDrivers {
    background-color: color-mix(in srgb, var(--mantine-color-gray-0) 80%, transparent);
}

/* The waiting lane carries its own tint, so it steps one shade darker instead. */
.dispatchRowNotifyDrivers.dispatchRowAlternate {
    background-color: color-mix(in srgb, var(--mantine-color-gray-1) 70%, transparent);
}

.accentBar {
    align-self: stretch;
    border-radius: 1px;
}

.accentNeedsAction {
    width: 4px;
    background-color: var(--mt-signal-attention);
}

.accentNotifyDrivers {
    width: 4px;
    background-color: var(--mt-signal-pending-2);
}

.accentDispatched {
    width: 4px;
    background-color: var(--mt-signal-positive-2);
}

.identityBlock {
    min-width: 0;
}

.identityTitle {
    font-size: var(--mantine-font-size-sm);
    font-weight: 600;
    line-height: 1.3;
    color: var(--mantine-color-dark-7);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.identityMeta {
    font-size: var(--mantine-font-size-xs);
    line-height: 1.3;
    color: var(--mantine-color-gray-7);
    margin-top: 0.1rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.identityJobCode,
.identityCustomer,
.identityRequirementSummary {
    overflow: hidden;
    text-overflow: ellipsis;
}

.identityCustomer {
    font-weight: 600;
    color: var(--mantine-color-dark-6);
}

.identityMetaSeparator {
    color: var(--mantine-color-gray-5);
}

.statusBlock {
    min-width: 0;
    /* Truncate the summary when the schedule column takes the space it needs,
       rather than letting the two lines collide. */
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.statusNeedsAction {
    font-size: var(--mantine-font-size-sm);
    line-height: 1.35;
    font-weight: 600;
    color: var(--mantine-color-dark-7);
}

.statusSignalIntervention {
    font-size: var(--mantine-font-size-sm);
    line-height: 1.35;
    font-weight: 600;
    color: var(--mantine-color-dark-7);
}

.statusSignalAttention {
    font-size: var(--mantine-font-size-sm);
    line-height: 1.35;
    font-weight: 500;
    color: var(--mantine-color-gray-8);
}

.statusSignalInformational {
    font-size: var(--mantine-font-size-sm);
    line-height: 1.35;
    font-weight: 400;
    font-style: italic;
    color: var(--mantine-color-gray-6);
}

.statusWaiting {
    font-size: var(--mantine-font-size-sm);
    line-height: 1.35;
    font-weight: 500;
    font-style: italic;
    color: var(--mantine-color-gray-7);
}

.statusPrimaryQuiet {
    font-size: var(--mantine-font-size-sm);
    line-height: 1.35;
    font-weight: 400;
    color: var(--mantine-color-gray-6);
}

.scheduleBlock {
    text-align: right;
    min-width: 0;
}

.scheduleAnchorGroup {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.3rem;
    max-width: 100%;
    white-space: nowrap;
}

.scheduleStatus {
    display: inline-flex;
    align-items: center;
    padding: 0.08rem 0.3rem;
    border: 1px solid var(--mt-signal-attention-2);
    border-radius: var(--mantine-radius-sm);
    background-color: var(--mt-signal-attention-0);
    color: var(--mt-signal-attention-8);
    font-size: 0.6rem;
    line-height: 1.2;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
}

.scheduleAnchor {
    font-family: var(--mantine-font-family-monospace);
    font-size: var(--mantine-font-size-xs);
    color: var(--mantine-color-gray-7);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

.scheduleImminent {
    font-weight: 700;
    color: var(--mt-signal-attention-8);
}

.scheduleToday {
    font-weight: 600;
    color: var(--mantine-color-gray-8);
}

.scheduleFuture {
    font-weight: 400;
    color: var(--mantine-color-gray-6);
}

.scheduleContinuation {
    font-weight: 400;
    color: var(--mantine-color-gray-5);
}

.dispatchRowContinuation {
    opacity: 0.85;
}

.scheduleDuration {
    font-size: 0.65rem;
    line-height: 1.25;
    color: var(--mt-signal-attention-7);
    font-weight: 600;
    margin-top: 0.1rem;
    white-space: nowrap;
}

.scheduleTime {
    font-size: 0.65rem;
    line-height: 1.25;
    color: var(--mantine-color-gray-7);
    font-weight: 400;
    margin-top: 0.1rem;
    white-space: nowrap;
}

.rowChevron {
    font-size: 1rem;
    line-height: 1;
    color: var(--mantine-color-gray-4);
    text-align: center;
    transition:
        color 100ms ease,
        transform 100ms ease;
}

.dayGroup {
    border-bottom: 1px solid var(--mantine-color-gray-2);
}

.dayGroup:last-child {
    border-bottom: none;
}

.dayGroupHeader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.45rem 0.75rem;
    background-color: var(--mantine-color-white);
    border-bottom: 1px solid var(--mantine-color-gray-1);
}

.dayGroupLabel {
    font-size: var(--mantine-font-size-xs);
    font-weight: 700;
    letter-spacing: 0.02em;
    color: var(--mantine-color-gray-8);
}

.sectionHeader {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-height: var(--dispatch-lane-header-height, 1.875rem);
    padding: 0.4rem 1rem;
    margin-top: 0.75rem;
    background-color: var(--mantine-color-white);
    box-sizing: border-box;
}

.sectionHeaderNeedsAction {
    background-color: var(--mt-signal-attention-0);
}

.sectionHeaderNotifyDrivers {
    background-color: var(--mt-signal-pending-0);
}

.sectionHeaderDispatched {
    background-color: var(--mt-signal-positive-0);
}

.sectionHeaderLeading {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    min-width: 0;
}

.sectionAccent {
    width: 2px;
    height: 0.7rem;
    border-radius: 1px;
    flex-shrink: 0;
}

.sectionAccentNeedsAction {
    background-color: var(--mt-signal-attention-5);
}

.sectionAccentNotifyDrivers {
    background-color: var(--mt-signal-pending-5);
}

.sectionAccentDispatched {
    background-color: var(--mt-signal-positive-5);
}

.sectionLabel {
    font-size: 1rem;
    line-height: 1.2;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--mantine-color-gray-8);
}

.sectionCount {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.15rem;
    height: 1.15rem;
    padding: 0 0.5rem;
    border-radius: var(--mantine-radius-xl);
    background-color: var(--mantine-color-gray-1);
    font-family: var(--mantine-font-family-monospace);
    font-size: 0.8rem;
    line-height: 1;
    font-weight: 700;
    color: var(--mantine-color-gray-7);
    font-variant-numeric: tabular-nums;
}

.sectionCountNeedsAction {
    background-color: var(--mt-signal-attention-1);
    color: var(--mt-signal-attention-8);
}

.sectionCountNotifyDrivers {
    background-color: var(--mt-signal-pending-0);
    color: var(--mantine-color-gray-7);
}

.sectionCountDispatched {
    background-color: var(--mt-signal-positive-0);
    color: var(--mt-signal-positive-8);
}

.boardShell {
    width: 100%;
    background-color: var(--mantine-color-white);
}

.laneEmptyMessage {
    margin: 0;
    padding: 0.55rem 0.75rem 0.35rem calc(0.75rem + 2px + 0.35rem);
    font-size: var(--mantine-font-size-xs);
    line-height: 1.4;
    color: var(--mantine-color-dimmed);
}

.boardEmptyState {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 3.5rem 1.5rem 4rem;
    min-height: 12rem;
}

.boardEmptyTitle {
    font-size: var(--mantine-font-size-sm);
    font-weight: 600;
    color: var(--mantine-color-gray-7);
    margin-bottom: 0.35rem;
}

.boardEmptyBody {
    font-size: var(--mantine-font-size-sm);
    color: var(--mantine-color-gray-6);
    max-width: 22rem;
    line-height: 1.5;
}

/* The second selector keeps the highlight ahead of the zebra stripe. */
.highlighted,
.dispatchRow.highlighted {
    background-color: var(--mt-signal-pending-0);
}

.sectionHeaderCleanUp {
    background-color: var(--mantine-color-gray-1);
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.sectionAccentCleanUp {
    background-color: var(--mantine-color-gray-5);
}

.sectionCountCleanUp {
    background-color: var(--mantine-color-gray-2);
    color: var(--mantine-color-gray-8);
}
